/**
 * CoachPlaybook - Roster Component
 * Version: 0.5.145
 * Description: Mobile-optimized team roster with configurable fields
 * 
 * v0.5.119 Changes:
 * - Default jersey (isDefault) now reflects in roster header and all player cards
 * - Priority: isDefault > activeTemplateId > first template > DEFAULT_JERSEY_TEMPLATE
 */

import React, { useState, useEffect } from 'react';
import PlayerCard from './PlayerCard';
import PlayerForm from './PlayerForm';
import JerseyIcon, { DEFAULT_JERSEY_TEMPLATE } from '../common/JerseyIcon';
import { getSetting } from '../../utils/db';
import {
  getAllPlayers,
  addPlayer,
  updatePlayer,
  deletePlayer,
  searchPlayers,
  filterPlayersByPosition,
  POSITIONS
} from '../../utils/players';

const ROSTER_FIELDS_KEY = 'rosterFieldSettings';

const DEFAULT_FIELD_SETTINGS = {
  showHeight: true,
  showGrade: true,
  showGraduationYear: true,
  showNotes: true
};

const Roster = ({ teamName = '', jerseyTemplates = [], activeTemplateId = 'default' }) => {
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [message, setMessage] = useState(null);
  const [fieldSettings, setFieldSettings] = useState(DEFAULT_FIELD_SETTINGS);

  // Load players and field settings
  useEffect(() => {
    loadData();
  }, []);

  // Apply filters
  useEffect(() => {
    let result = players;
    result = searchPlayers(result, searchTerm);
    result = filterPlayersByPosition(result, positionFilter);
    setFilteredPlayers(result);
  }, [players, searchTerm, positionFilter]);

  const loadData = async () => {
    setLoading(true);
    
    // Load players
    const loadedPlayers = await getAllPlayers();
    setPlayers(loadedPlayers);
    
    // Load field settings
    const savedSettings = await getSetting(ROSTER_FIELDS_KEY);
    if (savedSettings) {
      setFieldSettings({ ...DEFAULT_FIELD_SETTINGS, ...savedSettings });
    }
    
    setLoading(false);
  };

  const handleAddPlayer = () => {
    setEditingPlayer(null);
    setShowForm(true);
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setShowForm(true);
  };

  const handleSavePlayer = async (playerData) => {
    let result;
    
    if (editingPlayer) {
      result = await updatePlayer(editingPlayer.id, playerData);
      showMessage(result.success ? 'Player updated successfully' : result.error, result.success ? 'success' : 'error');
    } else {
      result = await addPlayer(playerData);
      showMessage(result.success ? 'Player added successfully' : result.error, result.success ? 'success' : 'error');
    }

    if (result.success) {
      await loadData(); // Reload to get any field setting changes
      setShowForm(false);
      setEditingPlayer(null);
    }
  };

  const handleDeleteClick = (player) => {
    setPlayerToDelete(player);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (playerToDelete) {
      const result = await deletePlayer(playerToDelete.id);
      showMessage(result.success ? 'Player deleted' : result.error, result.success ? 'success' : 'error');
      
      if (result.success) {
        await loadData();
      }
      
      setShowDeleteConfirm(false);
      setPlayerToDelete(null);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading roster...</p>
        </div>
      </div>
    );
  }

  // Get the active jersey template - prefer isDefault, then activeTemplateId, then first
  const activeTemplate = jerseyTemplates.find(t => t.isDefault) || 
                         jerseyTemplates.find(t => t.id === activeTemplateId) || 
                         jerseyTemplates[0] ||
                         DEFAULT_JERSEY_TEMPLATE;

  return (
    <div data-element="roster-root" className="h-full flex flex-col bg-slate-900">
      {/* Header */}
      <div data-element="roster-header" className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700 px-4 py-4 md:px-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {/* v0.5.67: Team Jersey Preview */}
            <div data-element="roster-header-jersey" className="hidden md:block">
              <JerseyIcon 
                size={56}
                number={0}
                view="front"
                teamName={teamName}
                fillColor={activeTemplate.fillColor}
                outlineColor={activeTemplate.outlineColor}
                numberColor={activeTemplate.numberColor}
                numberOutline={activeTemplate.numberOutline}
                numberOutline2={activeTemplate.numberOutline2}
                textColor={activeTemplate.textColor}
                textOutline={activeTemplate.textOutline}
                textOutline2={activeTemplate.textOutline2}
                fontFamily={activeTemplate.fontFamily}
                numberY={activeTemplate.numberY ?? 55}
                numberSize={activeTemplate.numberSize ?? 100}
                nameY={activeTemplate.nameY ?? 5}
                nameSize={activeTemplate.nameSize ?? 100}
                jerseyBorderWeight={activeTemplate.jerseyBorderWeight ?? 12}
                numberBorderWeight={activeTemplate.numberBorderWeight ?? 6}
                numberBorderWeight2={activeTemplate.numberBorderWeight2 ?? 0}
                nameBorderWeight={activeTemplate.nameBorderWeight ?? 4}
                nameBorderWeight2={activeTemplate.nameBorderWeight2 ?? 0}
              />
            </div>
            <div>
              <h1 data-element="roster-header-title" className="text-2xl font-bold text-white">Team Roster</h1>
              <p data-element="roster-header-count" className="text-sm text-slate-400">{players.length} players</p>
            </div>
          </div>
          <button
            data-element="roster-add-btn"
            onClick={handleAddPlayer}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium hover:from-blue-500 hover:to-blue-600 transition-all shadow-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden md:inline">Add Player</span>
          </button>
        </div>

        {/* Search & Filter */}
        <div data-element="roster-filters" className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <input
              data-element="roster-search-input"
              type="text"
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          
          <select
            data-element="roster-position-filter"
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
            className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Positions</option>
            {Object.entries(POSITIONS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Message Toast */}
      {message && (
        <div data-element="roster-message" className={`mx-4 mt-4 px-4 py-3 rounded-lg ${
          message.type === 'success' ? 'bg-green-900/50 text-green-300 border border-green-700' : 'bg-red-900/50 text-red-300 border border-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Player List - Scrollable Container */}
      <div data-element="roster-player-list" className="flex-1 overflow-y-auto p-4 md:p-6">
        {filteredPlayers.length === 0 ? (
          <div data-element="roster-empty-state" className="text-center py-12">
            <svg className="w-16 h-16 text-slate-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 data-element="roster-empty-title" className="text-lg font-medium text-white mb-1">
              {players.length === 0 ? 'No players yet' : 'No players found'}
            </h3>
            <p data-element="roster-empty-message" className="text-slate-400 mb-4">
              {players.length === 0 
                ? 'Add your first player to get started'
                : 'Try adjusting your search or filters'}
            </p>
            {players.length === 0 && (
              <button
                data-element="roster-empty-add-btn"
                onClick={handleAddPlayer}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium hover:from-blue-500 hover:to-blue-600 transition-all shadow-lg"
              >
                Add First Player
              </button>
            )}
          </div>
        ) : (
          <div data-element="roster-player-cards" className="space-y-3 pb-20">
            {filteredPlayers.map((player) => (
              <PlayerCard
                key={player.id}
                player={player}
                teamName={teamName}
                jerseyTemplates={jerseyTemplates}
                activeTemplateId={activeTemplateId}
                fieldSettings={fieldSettings}
                onEdit={handleEditPlayer}
                onDelete={handleDeleteClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Player Form Modal */}
      {showForm && (
        <div data-element="roster-player-form-modal">
          <PlayerForm
            player={editingPlayer}
            players={players}
            jerseyTemplates={jerseyTemplates}
            activeTemplateId={activeTemplateId}
            onSave={handleSavePlayer}
            onCancel={() => {
              setShowForm(false);
              setEditingPlayer(null);
              loadData(); // Reload in case field settings changed
            }}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && playerToDelete && (
        <div data-element="roster-delete-modal" className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div data-element="roster-delete-modal-content" className="bg-slate-800 rounded-xl p-6 max-w-sm w-full border border-slate-700 shadow-2xl">
            <h3 data-element="roster-delete-title" className="text-lg font-bold text-white mb-2">Delete Player?</h3>
            <p data-element="roster-delete-message" className="text-slate-400 mb-6">
              Are you sure you want to delete {playerToDelete.firstName} {playerToDelete.lastName}? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                data-element="roster-delete-cancel-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPlayerToDelete(null);
                }}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                data-element="roster-delete-confirm-btn"
                onClick={handleConfirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roster;
